// ---------------------------------------------------------------------------
// What the cashier sees after a sale rung up with no connection.
//
// It deliberately does NOT look like the normal receipt, and it does not
// invent a receipt number. Receipt numbers come from the server, in order, and
// a number made up on the device would eventually collide with a real one -
// two different sales sharing a number is the kind of thing that is only
// discovered during an argument about money.
//
// So the sale is identified by its own offline id, the screen says plainly
// that it is saved here and not yet sent, and the customer can be given a
// hand-written note if they want one. Being honest about what has happened is
// worth more than a receipt that looks complete and is not.
// ---------------------------------------------------------------------------

import { CheckCircle2, CloudOff } from 'lucide-react';
import { useI18n } from '../../i18n/I18nProvider';
import { formatUsd } from '../../utils/format';

export function OfflineSaleConfirmation({
  offlineId,
  totalUsd,
  onDone,
}: {
  offlineId: string;
  totalUsd: string;
  onDone: () => void;
}) {
  const { t } = useI18n();

  return (
    <div className="card p-6 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-butcher-green/10">
        <CheckCircle2 className="h-6 w-6 text-butcher-green" aria-hidden="true" />
      </div>

      <h3 className="mt-3 text-base font-semibold text-ink">{t.offline.soldOffline}</h3>
      <p className="numeric mt-1 text-2xl font-semibold text-ink">{formatUsd(totalUsd)}</p>

      <p className="mx-auto mt-3 max-w-sm text-sm text-ink-muted">{t.offline.soldOfflineBody}</p>

      <div className="mx-auto mt-4 flex max-w-sm items-center justify-center gap-2 rounded-control bg-antique-gold/10 px-3 py-2 text-xs text-antique-gold">
        <CloudOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="font-mono" dir="ltr">
          {offlineId}
        </span>
      </div>

      <button type="button" className="btn-primary mt-5" onClick={onDone} autoFocus>
        {t.offline.newOrder}
      </button>
    </div>
  );
}
